import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const CrownsAndBridges = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Crowns & Bridges Fort Worth TX | Dental Restorations | Fort Worth Dental</title>
        <meta name="description" content="Custom dental crowns and bridges in Fort Worth TX. Restore damaged teeth and replace missing teeth with natural-looking restorations. Call (817) 985-3337." />
        <meta name="keywords" content="dental crowns fort worth, dental bridges fort worth tx, tooth restoration, dental caps, tooth bridge, missing teeth replacement" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-2.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Crowns & Bridges in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Restore Your Smile with Custom Dental Restorations</p>
            <p className="text-sm mt-4 opacity-90">Dental Crowns | Tooth Bridges | Restorative Dentistry</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Understanding Dental Crowns */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/crowns-new-1.jpg" 
                  alt="Dental Crowns" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-2.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Understanding Dental Crowns: Purpose, Types & Benefits
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Dental crowns, also called "caps," are tooth-shaped restorations that cover and protect a damaged or weakened tooth. They restore the tooth's shape, size, strength, and appearance. Crowns can be made from various materials including porcelain, ceramic, metal alloys, or a combination. Porcelain and ceramic crowns are popular because they can be matched to the color of your natural teeth. At Fort Worth Dental, we create custom crowns that look natural and function like your real teeth.
                </p>
              </div>
            </div>
          </section>

          {/* When Do I Need A Crown */}
          <section className="mb-16 bg-teal-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">When Do I Need A Crown?</h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Your dentist may recommend a dental crown for several reasons:
            </p>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>To protect a weak tooth from breaking or to hold together parts of a cracked tooth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>To restore an already broken tooth or a tooth that has been severely worn down</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>To cover and support a tooth with a large filling when there isn't enough natural tooth remaining</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>To hold a dental bridge in place</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>To cover misshapen or severely discolored teeth for cosmetic improvement</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>To cover a dental implant</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>To cover a tooth that has had root canal treatment</span>
              </li>
            </ul>
          </section>

          {/* What Is A Dental Bridge */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is A Dental Bridge?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  A dental bridge literally bridges the gap created by one or more missing teeth. A bridge consists of two or more crowns for the teeth on either side of the gap (called abutment teeth) and a false tooth/teeth in between (called pontics). Dental bridges are supported by natural teeth or dental implants.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Bridges can be made from gold, alloys, porcelain, or a combination of these materials. Porcelain bridges can be matched to the color of your natural teeth, making them virtually invisible.
                </p>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/crowns-new-2.jpg" 
                  alt="Dental Bridges" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Bridge Benefits */}
          <section className="mb-16 bg-gradient-to-br from-purple-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Bridges Produce The Following Benefits:</h2>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Restore your smile and facial structure</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Restore your ability to properly chew and speak</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Maintain the shape of your face</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Distribute the forces in your bite properly by replacing missing teeth</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-3 text-xl">✓</span>
                <span>Prevent remaining teeth from drifting out of position</span>
              </li>
            </ul>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Crowns & Bridges?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced team uses the latest technology and techniques to provide comfortable, effective treatment.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Board Certified</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Crowns & Bridges Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about crowns and bridges in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Crowns & Bridges Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CrownsAndBridges;